import "./style.css";
import { applyDomAssetUrls } from "./assets/apply-dom-asset-urls.js";
import { CrazyGamesBridge } from "./core/crazygames-bridge.js";
import { PlaygamaBridge } from "./core/playgama-bridge.js";
import { Y8Bridge } from "./core/y8-bridge.js";
import { YtBridge } from "./core/yt-bridge.js";
import { PokiBridge } from "./core/poki-bridge.js";
import { GdBridge } from "./core/gd-bridge.js";
import { FrvrBridge } from "./core/frvr-bridge.js";
import { buildGameOverSharePayload } from "./core/frvr-share.js";
import { resolveGameStorage } from "./core/persistence-backend.js";
import { SoundManager } from "./core/sound-manager.js";
import { EventBus } from "./core/event-bus.js";
import { Game } from "./core/game.js";
import { bootstrapFirebaseAnalytics } from "./analytics/index.js";
import { createLeaderboardClient } from "./leaderboard/index.js";
import { readLayoutModePreference } from "./layout/layout-mode.js";
import {
  StorageSlot,
  getStorageKey,
} from "./core/storage.js";
import { formatAppVersionLabel, getAppVersion } from "./config/app-version.js";
import { applyPrivacyPolicyLinkVisibility } from "./config/privacy-policy.js";
import { installUiButtonClickSfx } from "./core/ui-click-sfx.js";
import { dismissGameLoader, markGameLoading, setGameLoaderBehindAd } from "./core/game-loader.js";
import { installEmbedScrollGuard } from "./core/embed-scroll-guard.js";
import { wireDevPerfDialog } from "./core/dev-perf-dialog.js";
import { getBuildVariant } from "./core/player-stats.js";
import { devLog } from "./core/dev-log.js";
import {
  INTERSTITIAL_AD_NAME_PLAY_AGAIN,
} from "./config/ad-placements.js";

/**
 * MERGE RUN — 2-stage hybrid game
 * Thin wiring: creates Game, wires DOM, calls init.
 */

var PROGRESS_STORAGE_KEY = getStorageKey(StorageSlot.PROGRESS);
var ECONOMY_STORAGE_KEY = getStorageKey(StorageSlot.ECONOMY);
var BEST_SCORE_STORAGE_KEY = getStorageKey(StorageSlot.BEST_SCORE);

async function syncPlaygamaStorageToLocal() {
  if (!PlaygamaBridge.isEnabled()) return;
  var keys = [PROGRESS_STORAGE_KEY, ECONOMY_STORAGE_KEY, BEST_SCORE_STORAGE_KEY];
  var values = await PlaygamaBridge.storageGet(keys);
  if (!Array.isArray(values)) return;
  for (var i = 0; i < keys.length; i++) {
    var v = values[i];
    if (v == null) continue;
    var stored = typeof v === "string" ? v : JSON.stringify(v);
    try {
      localStorage.setItem(keys[i], stored);
    } catch (e) {
      console.warn("[main] Playgama storage → localStorage failed:", e);
    }
  }
}

async function syncY8StorageToLocal() {
  if (!Y8Bridge.isEnabled()) return;
  await Y8Bridge.syncCloudToLocal(PROGRESS_STORAGE_KEY, BEST_SCORE_STORAGE_KEY, ECONOMY_STORAGE_KEY);
}

function scheduleYtFirstFrameReady() {
  if (typeof requestAnimationFrame === "function") {
    requestAnimationFrame(function () {
      YtBridge.notifyFirstFrameReady();
    });
  } else {
    YtBridge.notifyFirstFrameReady();
  }
}

async function bootstrap() {
  "use strict";

  markGameLoading();
  installEmbedScrollGuard();
  PokiBridge.installPageJumpGuard();
  applyDomAssetUrls();

  var gdPortalBuild = import.meta.env.VITE_WITH_GAMEDISTRIBUTION === "true";
  var frvrPortalBuild = import.meta.env.VITE_WITH_FRVR === "true";
  var analyticsBootstrap = (YtBridge.isEnabled() || PokiBridge.isEnabled() || gdPortalBuild || frvrPortalBuild)
    ? Promise.resolve(null)
    : bootstrapFirebaseAnalytics();
  var crazygamesUsername = null;
  var y8Nickname = null;

  var cgBridge = CrazyGamesBridge;
  var pgBridge = PlaygamaBridge;
  var y8Bridge = Y8Bridge;
  var ytBridge = YtBridge;
  var pokiBridge = PokiBridge;
  var gdBridge = GdBridge;
  var frvrBridge = FrvrBridge;

  try {
    await Promise.all([
      cgBridge && typeof cgBridge.ensureInitialized === "function"
        ? cgBridge.ensureInitialized()
        : Promise.resolve(),
      pgBridge && typeof pgBridge.ensureInitialized === "function"
        ? pgBridge.ensureInitialized()
        : Promise.resolve(),
      y8Bridge && typeof y8Bridge.ensureInitialized === "function"
        ? y8Bridge.ensureInitialized()
        : Promise.resolve(),
      ytBridge && typeof ytBridge.ensureInitialized === "function"
        ? ytBridge.ensureInitialized()
        : Promise.resolve(),
      pokiBridge && typeof pokiBridge.ensureInitialized === "function"
        ? pokiBridge.ensureInitialized()
        : Promise.resolve(),
      gdBridge && typeof gdBridge.ensureInitialized === "function"
        ? gdBridge.ensureInitialized()
        : Promise.resolve(),
      frvrBridge && typeof frvrBridge.ensureInitialized === "function"
        ? frvrBridge.ensureInitialized()
        : Promise.resolve(),
    ]);
    if (y8Bridge && y8Bridge.isEnabled && y8Bridge.isEnabled()) {
      var blocked = await y8Bridge.checkBlacklisted();
      if (blocked) {
        y8Bridge.showBlacklistOverlay();
        return;
      }
    }
    if (cgBridge && cgBridge.isEnabled && cgBridge.isEnabled()) cgBridge.loadingStart();
    if (!ytBridge.isEnabled || !ytBridge.isEnabled()) {
      await syncPlaygamaStorageToLocal();
      await syncY8StorageToLocal();
    }
    if (frvrBridge && frvrBridge.isEnabled && frvrBridge.isEnabled()) {
      await frvrBridge.installGameStorage();
      await frvrBridge.refreshRewardedAdReady();
      await frvrBridge.seedLeaderboardPlayerId(resolveGameStorage(null));
      var frvrPlayerId = await frvrBridge.getPlayerId();
      if (frvrPlayerId) {
        devLog("[main] FRVR player id:", frvrPlayerId);
      }
    }
    if (ytBridge.isEnabled && ytBridge.isEnabled()) {
      scheduleYtFirstFrameReady();
    }
    var playgamaLang = pgBridge && typeof pgBridge.getLanguage === "function" ? pgBridge.getLanguage() : null;
    if (playgamaLang) {
      devLog("[main] Playgama platform language:", playgamaLang);
    }
    y8Nickname = y8Bridge && typeof y8Bridge.getNickname === "function" ? y8Bridge.getNickname() : null;
    if (y8Nickname) {
      devLog("[main] Y8 nickname:", y8Nickname);
    }
    crazygamesUsername =
      cgBridge && typeof cgBridge.getUsername === "function" ? await cgBridge.getUsername() : null;
    if (crazygamesUsername) {
      devLog("[main] CrazyGames username:", crazygamesUsername);
    }
    if (cgBridge && typeof cgBridge.fetchAdblockStatus === "function") {
      var adblock = await cgBridge.fetchAdblockStatus();
      if (adblock) {
        devLog("[main] CrazyGames adblock detected — rewarded revive disabled");
      }
    }
  } catch (e) {
    console.warn("[main] platform init:", e);
  }

  var gameStorage = resolveGameStorage(null);
  var leaderboardClient = createLeaderboardClient({
    crazygamesUsername: crazygamesUsername,
    y8Nickname: y8Nickname,
    storage: gameStorage,
  });
  leaderboardClient.getPlayerId();

  try {
    await analyticsBootstrap;
  } catch (e) {
    console.warn("[main] analytics bootstrap:", e);
  }

  var soundManager = new SoundManager();
  soundManager.init();
  CrazyGamesBridge.installMuteSync(soundManager);
  if (!YtBridge.isEnabled()) {
    PlaygamaBridge.installAudioSync(soundManager);
  }
  Y8Bridge.installAdAudioSync(soundManager);
  YtBridge.installAudioSync(soundManager);
  GdBridge.installAdAudioSync(soundManager);
  FrvrBridge.installAudioSync(soundManager);

  if (gdBridge && gdBridge.isEnabled && gdBridge.isEnabled()) {
    setGameLoaderBehindAd(true);
    try {
      await gdBridge.waitForInitialGameStart();
    } finally {
      setGameLoaderBehindAd(false);
    }
  }

  var canvas = document.getElementById("gameCanvas");
  var game = new Game({
    canvas: canvas,
    usePixi: true,
    soundManager: soundManager,
    leaderboard: leaderboardClient,
    disableAnalytics: YtBridge.isEnabled() || GdBridge.isEnabled() || gdPortalBuild
      || FrvrBridge.isEnabled() || frvrPortalBuild,
    layoutModePreference: readLayoutModePreference(gameStorage),
    onProgressSaved: function (key, value) {
      if (PlaygamaBridge.isEnabled()) {
        PlaygamaBridge.storageSet(key, value);
      }
      if (Y8Bridge.isEnabled()) {
        Y8Bridge.relayProgressSaved(key, value, PROGRESS_STORAGE_KEY, BEST_SCORE_STORAGE_KEY, ECONOMY_STORAGE_KEY);
      }
      if (YtBridge.isEnabled()) {
        YtBridge.relayProgressSaved();
      }
      if (FrvrBridge.isEnabled() && typeof FrvrBridge.flushGameStorage === "function") {
        FrvrBridge.flushGameStorage();
      }
    },
  });

  PlaygamaBridge.installPauseSync(game);
  YtBridge.installPauseSync(game);
  GdBridge.installPauseSync(game);
  FrvrBridge.installPauseSync(game);

  function ensureAudioUnlocked(fromUserGesture) {
    if (typeof soundManager.unlock === "function") {
      soundManager.unlock({ fromUserGesture: !!fromUserGesture });
    }
  }

  installUiButtonClickSfx(document.getElementById("app"));

  var dom = {
    endOverlay: document.getElementById("endOverlay"),
    gameOverOverlay: document.getElementById("gameOverOverlay"),
    gameOverScoreCard: document.getElementById("gameOverScoreCard"),
    gameOverScoreTitle: document.getElementById("gameOverScoreTitle"),
    gameOverScoreNewBest: document.getElementById("gameOverScoreNewBest"),
    gameOverScoreNewBestValue: document.getElementById("gameOverScoreNewBestValue"),
    gameOverScoreRows: document.getElementById("gameOverScoreRows"),
    gameOverScoreValue: document.getElementById("gameOverScoreValue"),
    gameOverBestScoreValue: document.getElementById("gameOverBestScoreValue"),
    leaderboardCard: document.getElementById("leaderboardCard"),
    leaderboardStatus: document.getElementById("leaderboardStatus"),
    leaderboardPanel: document.getElementById("leaderboardPanel"),
    leaderboardLoading: document.getElementById("leaderboardLoading"),
    leaderboardBody: document.getElementById("leaderboardBody"),
    leaderboardOwnTable: document.getElementById("leaderboardOwnTable"),
    leaderboardOwnBody: document.getElementById("leaderboardOwnBody"),
    leaderboardRankHint: document.getElementById("gameOverRankHint"),
    gameOverShareBtn: document.getElementById("gameOverShareBtn"),
    gameOverEvoStripMount: document.getElementById("gameOverEvoStripMount"),
    playerHud: document.getElementById("playerHud"),
    hudLives: document.getElementById("hudLives"),
    gemBadge: document.getElementById("gemBadge"),
    gemCount: document.getElementById("gemCount"),
    fallHudStrip: document.getElementById("fallHudStrip"),
    fallRunStrip: document.getElementById("fallRunStrip"),
    fallPauseBtn: document.getElementById("fallPauseBtn"),
    fallVolumeBtn: document.getElementById("fallVolumeBtn"),
    fallVolumeOverlay: document.getElementById("fallVolumeOverlay"),
    fallVolumeBgm: document.getElementById("fallVolumeBgm"),
    fallVolumeSfx: document.getElementById("fallVolumeSfx"),
    fallVolumePlatformMute: document.getElementById("fallVolumePlatformMute"),
    fallVolumeOk: document.getElementById("fallVolumeOk"),
    dailyBonusBtn: document.getElementById("dailyBonusBtn"),
    wheelBtn: document.getElementById("wheelBtn"),
    dailyBonusOverlay: document.getElementById("dailyBonusOverlay"),
    dailyBonusGrid: document.getElementById("dailyBonusGrid"),
    dailyBonusClaimBtn: document.getElementById("dailyBonusClaimBtn"),
    dailyBonusClaimLabel: document.getElementById("dailyBonusClaimLabel"),
    dailyBonusClaimCooldown: document.getElementById("dailyBonusClaimCooldown"),
    dailyBonusCloseBtn: document.getElementById("dailyBonusCloseBtn"),
    wheelOverlay: document.getElementById("wheelOverlay"),
    wheelDisc: document.getElementById("wheelDisc"),
    wheelRotator: document.getElementById("wheelRotator"),
    wheelLabels: document.getElementById("wheelLabels"),
    wheelFreeSpinBtn: document.getElementById("wheelFreeSpinBtn"),
    wheelFreeSpinLabel: document.getElementById("wheelFreeSpinLabel"),
    wheelAdSpinBtn: document.getElementById("wheelAdSpinBtn"),
    wheelAdSpinLabel: document.getElementById("wheelAdSpinLabel"),
    wheelFreeSpinCooldown: document.getElementById("wheelFreeSpinCooldown"),
    wheelAdSpinCooldown: document.getElementById("wheelAdSpinCooldown"),
    wheelSegmentDim: document.getElementById("wheelSegmentDim"),
    wheelRewardClaimBtn: document.getElementById("wheelRewardClaimBtn"),
    wheelSkipBtn: document.getElementById("wheelSkipBtn"),
    gemsAdOverlay: document.getElementById("gemsAdOverlay"),
    gemsAdWatchBtn: document.getElementById("gemsAdWatchBtn"),
    gemsAdCooldown: document.getElementById("gemsAdCooldown"),
    gemsAdSkipBtn: document.getElementById("gemsAdSkipBtn"),
    gemsAdRewardAmount: document.getElementById("gemsAdRewardAmount"),
    rewardRevealOverlay: document.getElementById("rewardRevealOverlay"),
    rewardRevealItems: document.getElementById("rewardRevealItems"),
    rewardRevealClaimBtn: document.getElementById("rewardRevealClaimBtn"),
    gemAdBtn: document.getElementById("gemAdBtn"),
    verticalDailyBonusBtn: document.getElementById("verticalDailyBonusBtn"),
    verticalWheelBtn: document.getElementById("verticalWheelBtn"),
    verticalGemAdBtn: document.getElementById("verticalGemAdBtn"),
    fallRightHudModule: document.getElementById("fallRightHudModule"),
    evoRingWrap: document.getElementById("evoRingWrap"),
    evoRingMount: document.getElementById("evoRingMount"),
    fallStashPanel: document.getElementById("fallStashPanel"),
    fallStashMount: document.getElementById("fallStashMount"),
    stageChromeRight: document.getElementById("stageChromeRight"),
    shakeActionsCol: document.getElementById("shakeActionsCol"),
    shakeHpRow: document.getElementById("shakeHpRow"),
    shakeHpPill: document.getElementById("shakeHpPill"),
    fallLogoPanel: document.getElementById("fallLogoPanel"),
    fallProgressDial: document.getElementById("fallProgressDial"),
    fallProgressDialDisc: document.getElementById("fallProgressDialDisc"),
    fallProgressDialDay: document.getElementById("fallProgressDialDay"),
    fallProgressDialDayInk: document.getElementById("fallProgressDialDayInk"),
    scorePanel: document.getElementById("scorePanel"),
    scoreValue: document.getElementById("scoreValue"),
    bestScoreValue: document.getElementById("bestScoreValue"),
    shakeScoreValue: document.getElementById("shakeScoreValue"),
    shakeBestScoreValue: document.getElementById("shakeBestScoreValue"),

    stageStatus: document.getElementById("stageStatus"),
    stageBadge: document.getElementById("stageBadge"),
    stageObjective: document.getElementById("stageObjective"),
    evolutionDockPromptOverlay: document.getElementById("evolutionDockPromptOverlay"),
    evolutionDockPromptInput: document.getElementById("evolutionDockPromptInput"),
    evolutionDockPromptOk: document.getElementById("evolutionDockPromptOk"),
    statusToast: document.getElementById("statusToast"),
    hud: document.getElementById("hud"),
    hudHpFill: document.getElementById("hudHpFill"),
    fallMoveHint: document.getElementById("fallMoveHint"),
    liveReviveOverlay: document.getElementById("liveReviveOverlay"),
    secretRevealOverlay: document.getElementById("secretRevealOverlay"),
    secretRevealPopoutMount: document.getElementById("secretRevealPopoutMount"),
    victoryOverlay: document.getElementById("victoryOverlay"),
    puzzleRevealOverlay: document.getElementById("puzzleRevealOverlay"),
    puzzleRevealHeadline: document.getElementById("puzzleRevealHeadline"),
    puzzleRevealBoard: document.getElementById("puzzleRevealBoard"),
    puzzleRevealCount: document.getElementById("puzzleRevealCount"),
    puzzleRevealOkBtn: document.getElementById("puzzleRevealOkBtn"),
    victoryDragonImg: document.getElementById("victoryDragonImg"),
    victoryHeadline: document.getElementById("victoryHeadline"),
    victoryFeedbackBlock: document.getElementById("victoryFeedbackBlock"),
    victoryFeedbackMount: document.getElementById("victoryFeedbackMount"),
    victoryContinueBtn: document.getElementById("victoryContinueBtn"),
    liveReviveTitle: document.getElementById("liveReviveTitle"),
    liveReviveBtn: document.getElementById("liveReviveBtn"),
    liveReviveSkipBtn: document.getElementById("liveReviveSkipBtn"),
    shakeDropHint: document.getElementById("shakeDropHint"),
    shakeHintTooltip: document.getElementById("shakeHintTooltip"),
    healHintTooltip: document.getElementById("healHintTooltip"),
    playerPauseHint: document.getElementById("playerPauseHint"),
    hostPauseOverlay: document.getElementById("hostPauseOverlay"),
    shakeUI: document.getElementById("shakeUI"),
    imageAdjustPanel: document.getElementById("imageAdjustPanel"),
    shakeCountEl: document.getElementById("shakeCountEl"),
    bombCountEl: document.getElementById("bombCount"),
    shakeBtn: document.getElementById("shakeBtn"),
    bombBtn: document.getElementById("bombBtn"),
    bombCellBtn: document.getElementById("bombCellBtn"),
    bombTripleBtn: document.getElementById("bombTripleBtn"),
    playChrome: document.getElementById("playChrome"),
    fallHpRow: document.getElementById("fallHpRow"),
    fallHpPill: document.getElementById("fallHpPill"),
    fallHudLives: document.getElementById("fallHudLives"),
    shakeHpPill: document.getElementById("shakeHpPill"),
    shakeJarChrome: document.getElementById("shakeJarChrome"),
    jarDropQueueStack: document.getElementById("jarDropQueueStack"),
    jarDropPreview: document.getElementById("jarDropPreview"),
    fixBtn: document.getElementById("shakeHpPill"),
    fixBtnCostEl: document.getElementById("fixBtnCostEl"),
    minimizeBtn: document.getElementById("minimizeBtn"),
    minimizeBtnCostEl: document.getElementById("minimizeBtnCostEl"),
    shakeScorePanel: document.getElementById("shakeScorePanel"),
    continueBtn: document.getElementById("continueBtn"),

    hudTime: document.getElementById("hudTime"),
    hudGems: document.getElementById("hudGems"),
    hudHp: document.getElementById("hudHp"),
    hudFlask: document.getElementById("hudFlask"),
    devFpsOverlay: document.getElementById("devFpsOverlay"),
    devStatsOverlay: document.getElementById("devStatsOverlay"),
    devPerfDialog: document.getElementById("devPerfDialog"),
    devPerfDialogBackdrop: document.getElementById("devPerfDialogBackdrop"),
    devPerfDialogCloseBtn: document.getElementById("devPerfDialogCloseBtn"),
    devPerfBgBtn: document.getElementById("devPerfBgBtn"),
    devPerfStarsBtn: document.getElementById("devPerfStarsBtn"),
    devPerfSkyBtn: document.getElementById("devPerfSkyBtn"),
    devPerfSoundsBtn: document.getElementById("devPerfSoundsBtn"),
    devPerfParticlesBtn: document.getElementById("devPerfParticlesBtn"),
    devPerfPopupsBtn: document.getElementById("devPerfPopupsBtn"),
    devPerfLayoutBtn: document.getElementById("devPerfLayoutBtn"),
    devPerfBoundsBtn: document.getElementById("devPerfBoundsBtn"),
    devPerfFallCollidersBtn: document.getElementById("devPerfFallCollidersBtn"),
    appVersionOverlay: document.getElementById("appVersionOverlay"),
    privacyPolicyLink: document.getElementById("privacyPolicyLink"),
    devTestBar: document.getElementById("devTestBar"),
    devTestReviveBtn: document.getElementById("devTestReviveBtn"),
    devTestLossBtn: document.getElementById("devTestLossBtn"),
    devTestWinBtn: document.getElementById("devTestWinBtn"),
    devTestDiscoverBtn: document.getElementById("devTestDiscoverBtn"),
    devTestResetBtn: document.getElementById("devTestResetBtn"),
    devTestPuzzleBtn: document.getElementById("devTestPuzzleBtn"),
    devTestSpeedBtn: document.getElementById("devTestSpeedBtn"),
    verticalChrome: document.getElementById("verticalChrome"),
    verticalHeader: document.getElementById("verticalHeader"),
    verticalHeaderPanel: document.getElementById("verticalHeaderPanel"),
    verticalDialSlot: document.getElementById("verticalDialSlot"),
    verticalScorePanel: document.getElementById("verticalScorePanel"),
    verticalScoreValue: document.getElementById("verticalScoreValue"),
    verticalBestScoreValue: document.getElementById("verticalBestScoreValue"),
    verticalPlayOverlays: document.getElementById("verticalPlayOverlays"),
    verticalSoundPauseCol: document.getElementById("verticalSoundPauseCol"),
    verticalFallVolumeBtn: document.getElementById("verticalFallVolumeBtn"),
    verticalFallPauseBtn: document.getElementById("verticalFallPauseBtn"),
    verticalGemSlot: document.getElementById("verticalGemSlot"),
    verticalGemBadge: document.getElementById("verticalGemBadge"),
    verticalGemCount: document.getElementById("verticalGemCount"),
    verticalMidRow: document.getElementById("verticalMidRow"),
    verticalStashPanel: document.getElementById("verticalStashPanel"),
    verticalStashMount: document.getElementById("verticalStashMount"),
    verticalShakeActionsRow: document.getElementById("verticalShakeActionsRow"),
    verticalShakeBtn: document.getElementById("verticalShakeBtn"),
    verticalShakeCountEl: document.getElementById("verticalShakeCountEl"),
    verticalBombCellBtn: document.getElementById("verticalBombCellBtn"),
    verticalBombTripleBtn: document.getElementById("verticalBombTripleBtn"),
    verticalMinimizeBtn: document.getElementById("verticalMinimizeBtn"),
    verticalMinimizeBtnCostEl: document.getElementById("verticalMinimizeBtnCostEl"),
    verticalContinueBtn: document.getElementById("verticalContinueBtn"),
    verticalEvoStripWrap: document.getElementById("verticalEvoStripWrap"),
    verticalEvoStripMount: document.getElementById("verticalEvoStripMount"),
  };

  if (dom.shakeBtn) dom.shakeBtn.addEventListener("click", function () { game.doShake(); });
  if (dom.shakeHintTooltip) {
    dom.shakeHintTooltip.addEventListener("click", function (e) {
      e.preventDefault();
      e.stopPropagation();
      game.dismissShakeHintForever();
    });
  }
  if (dom.bombBtn) dom.bombBtn.addEventListener("click", function () { game.doBomb(); });
  if (dom.bombCellBtn) dom.bombCellBtn.addEventListener("click", function () { game.doBombCells(); });
  if (dom.bombTripleBtn) dom.bombTripleBtn.addEventListener("click", function () { game.doBombFirstThree(); });
  if (dom.fixBtn) dom.fixBtn.addEventListener("click", function () { game.doFix(); });
  if (dom.minimizeBtn) {
    dom.minimizeBtn.addEventListener("click", function () {
      game.toggleMinimizeMode();
    });
  }

  if (dom.verticalShakeBtn) {
    dom.verticalShakeBtn.addEventListener("click", function () {
      if (dom.shakeBtn) dom.shakeBtn.click();
      else game.doShake();
    });
  }
  if (dom.verticalBombCellBtn) {
    dom.verticalBombCellBtn.addEventListener("click", function () {
      if (dom.bombCellBtn) dom.bombCellBtn.click();
      else game.doBombCells();
    });
  }
  if (dom.verticalBombTripleBtn) {
    dom.verticalBombTripleBtn.addEventListener("click", function () {
      if (dom.bombTripleBtn) dom.bombTripleBtn.click();
      else game.doBombFirstThree();
    });
  }
  if (dom.verticalMinimizeBtn) {
    dom.verticalMinimizeBtn.addEventListener("click", function () {
      if (dom.minimizeBtn) dom.minimizeBtn.click();
      else game.toggleMinimizeMode();
    });
  }

  if (dom.gameOverShareBtn) {
    dom.gameOverShareBtn.addEventListener("click", function () {
      if (!FrvrBridge.isEnabled() || !FrvrBridge.supportsShare()) return;
      var payload = buildGameOverSharePayload({
        score: game.currentScore,
        bestScore: game.bestScore,
      });
      FrvrBridge.shareMessage(payload).then(function (ok) {
        if (!ok) game.showToast("Share unavailable");
      });
    });
  }

  var playAgainBtn = document.getElementById("playAgainBtn");
  if (playAgainBtn) {
    playAgainBtn.addEventListener("click", function () {
      ensureAudioUnlocked(true);
      function startPlayAgain() {
        game.requestPlayAgain();
      }
      if (Y8Bridge.isEnabled() && Y8Bridge.shouldShowPlayAgainInterstitial(game)) {
        // Await ad close — do not start a new run under the interstitial.
        Y8Bridge.showBrowseInterstitial(INTERSTITIAL_AD_NAME_PLAY_AGAIN).then(
          startPlayAgain,
          startPlayAgain
        );
        return;
      }
      if (YtBridge.isEnabled() && YtBridge.shouldShowPlayAgainInterstitial(game)) {
        // Same Play Again placement as Y8; await SDK settle (shown or not) before the next run.
        YtBridge.requestInterstitialAd({ game: game }).then(
          startPlayAgain,
          startPlayAgain
        );
        return;
      }
      if (PokiBridge.isEnabled() && game.shouldShowPokiPlayAgainInterstitial()) {
        PokiBridge.commercialBreak({ game: game }).then(startPlayAgain, startPlayAgain);
        return;
      }
      if (GdBridge.isEnabled() && GdBridge.shouldShowPlayAgainInterstitial(game)) {
        GdBridge.runAfterNextGameStart(startPlayAgain);
        GdBridge.requestInterstitialAd({ game: game });
        return;
      }
      if (FrvrBridge.isEnabled() && FrvrBridge.shouldShowPlayAgainInterstitial(game)) {
        FrvrBridge.showBrowseInterstitial(INTERSTITIAL_AD_NAME_PLAY_AGAIN, { game: game }).then(
          startPlayAgain,
          startPlayAgain
        );
        return;
      }
      if (PlaygamaBridge.isEnabled()) {
        PlaygamaBridge.showInterstitial(INTERSTITIAL_AD_NAME_PLAY_AGAIN);
      }
      startPlayAgain();
    });
  }

  var fallPauseBtn = document.getElementById("fallPauseBtn");
  if (fallPauseBtn) {
    fallPauseBtn.addEventListener("click", function () {
      game.toggleFallPause();
    });
  }
  if (dom.verticalFallPauseBtn) {
    dom.verticalFallPauseBtn.addEventListener("click", function () {
      if (fallPauseBtn) fallPauseBtn.click();
      else game.toggleFallPause();
    });
  }
  if (dom.verticalFallVolumeBtn && dom.fallVolumeBtn) {
    dom.verticalFallVolumeBtn.addEventListener("click", function () {
      dom.fallVolumeBtn.click();
    });
  }

  var continueBtn = document.getElementById("continueBtn");
  if (continueBtn) {
    continueBtn.addEventListener("click", function () {
      EventBus.emit("run:continue");
    });
  }
  if (dom.verticalContinueBtn && continueBtn) {
    dom.verticalContinueBtn.addEventListener("click", function () {
      continueBtn.click();
    });
  }

  if (dom.liveReviveBtn) {
    dom.liveReviveBtn.addEventListener("click", function () {
      game.requestRevive();
    });
  }
  if (dom.liveReviveSkipBtn) {
    dom.liveReviveSkipBtn.addEventListener("click", function () {
      game.declineRevive("user");
    });
  }

  if (dom.puzzleRevealOkBtn) {
    dom.puzzleRevealOkBtn.addEventListener("click", function () {
      game.closePuzzleReveal();
    });
  }

  if (dom.appVersionOverlay) {
    dom.appVersionOverlay.textContent = formatAppVersionLabel(getAppVersion());
  }
  applyPrivacyPolicyLinkVisibility(dom.privacyPolicyLink, getBuildVariant());


  try {
    await game.attachPixiRenderer();
    game.init(dom);
    await game.maybeAutoStartFromMenu();
    if (import.meta.env.DEV) {
      if (dom.devFpsOverlay) {
        dom.devFpsOverlay.classList.remove("hidden");
        dom.devFpsOverlay.removeAttribute("aria-hidden");
      }
      if (dom.devStatsOverlay) {
        dom.devStatsOverlay.classList.remove("hidden");
        dom.devStatsOverlay.removeAttribute("aria-hidden");
      }
      if (dom.devTestBar) {
        dom.devTestBar.classList.remove("hidden");
        dom.devTestBar.removeAttribute("aria-hidden");
      }
      wireDevPerfDialog(game, dom);
      if (dom.devTestReviveBtn) {
        dom.devTestReviveBtn.addEventListener("click", function () {
          game.devPreviewReviveOverlay();
        });
      }
      if (dom.devTestLossBtn) {
        dom.devTestLossBtn.addEventListener("click", function () {
          game.devPreviewLossScreen();
        });
      }
      if (dom.devTestWinBtn) {
        dom.devTestWinBtn.addEventListener("click", function () {
          game.devPreviewVictoryPopup();
        });
      }
      if (dom.devTestDiscoverBtn) {
        dom.devTestDiscoverBtn.addEventListener("click", function () {
          if (typeof game.devPreviewDiscoveryReveal === "function") {
            game.devPreviewDiscoveryReveal();
          }
        });
      }
      if (dom.devTestResetBtn) {
        dom.devTestResetBtn.addEventListener("click", function () {
          if (typeof game.devResetProgress !== "function") return;
          game.devResetProgress();
          if (typeof game.syncDevTestBar === "function") game.syncDevTestBar();
        });
      }
      if (dom.devTestPuzzleBtn) {
        dom.devTestPuzzleBtn.addEventListener("click", function () {
          if (typeof game.devPreviewPuzzleReveal === "function") {
            game.devPreviewPuzzleReveal();
          }
        });
      }
      if (dom.devTestSpeedBtn) {
        dom.devTestSpeedBtn.addEventListener("click", function () {
          if (typeof game.devCycleFallSpeed !== "function") return;
          game.devCycleFallSpeed();
        });
      }
      if (typeof game.syncDevFallSpeedBtn === "function") game.syncDevFallSpeedBtn();
      if (typeof game.syncDevTestBar === "function") game.syncDevTestBar();
    }
    if (PlaygamaBridge.isEnabled()) {
      PlaygamaBridge.sendGameReady();
    }
  } finally {
    try {
      await dismissGameLoader();
    } catch (eDismiss) {
      console.warn("[main] game loader dismiss:", eDismiss);
    }
    if (FrvrBridge.isEnabled()) {
      await FrvrBridge.sendGameReady();
    }
    // YouTube: gameReady only after the loader is gone (interactable UI).
    if (YtBridge.isEnabled()) {
      YtBridge.notifyGameReady();
    }
    if (PokiBridge.isEnabled()) {
      PokiBridge.gameLoadingFinished();
    }
    try {
      if (cgBridge && cgBridge.isEnabled && cgBridge.isEnabled()) cgBridge.loadingStop();
    } catch (e2) {
      console.warn("[main] CrazyGames loadingStop:", e2);
    }
  }
}

bootstrap().catch(async function (err) {
  console.warn("[main] bootstrap failed:", err);
  try {
    await dismissGameLoader();
  } catch (eDismiss) {
    console.warn("[main] game loader dismiss:", eDismiss);
  }
});